import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Brain, RefreshCw, ArrowRight, Loader2, Sparkles, CheckCircle2, Clock, X, TrendingUp, Users, Trophy } from "lucide-react";

function ConfidenceBadge({ level }) {
  const styles = {
    High: "bg-emerald-100 text-emerald-800 border-emerald-200",
    Medium: "bg-amber-100 text-amber-800 border-amber-200",
    Low: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return (
    <Badge className={`border ${styles[level] || styles.Low} text-[10px] px-1.5 py-0`} data-testid={`intel-confidence-${level?.toLowerCase()}`}>
      {level || "Low"}
    </Badge>
  );
}

function CategoryPill({ category }) {
  const tones = {
    billing: "bg-rose-50 text-rose-700",
    planner: "bg-indigo-50 text-indigo-700",
    team: "bg-amber-50 text-amber-700",
    fundraiser: "bg-emerald-50 text-emerald-700",
    locations: "bg-sky-50 text-sky-700",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${tones[category] || "bg-slate-50 text-slate-600"}`}>
      {category}
    </span>
  );
}

export default function TapTrackIntelligence({ onNavigate, onOpenFundraiser }) {
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState(null); // insight id being acted on
  const navigate = useNavigate();
  const firstSeenMap = useRef({}); // insightId -> ISO timestamp first rendered

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [insightsRes, summaryRes] = await Promise.all([
        apiClient.get("/intelligence"),
        apiClient.get("/intelligence/summary").catch(() => ({ data: null })),
      ]);
      setData(insightsRes.data);
      setSummary(summaryRes.data);
      // stamp firstSeenAt for any newly visible insight
      const now = new Date().toISOString();
      (insightsRes.data?.insights || []).forEach(ins => {
        if (!firstSeenMap.current[ins.id]) firstSeenMap.current[ins.id] = now;
      });
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: e?.response?.data?.detail || "Failed to load" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleCta = (insight) => {
    const cta = insight.cta || {};
    const refs = insight.refs || {};
    if (cta.fundraiserId || refs.fundraiserId) {
      const fid = cta.fundraiserId || refs.fundraiserId;
      if (onOpenFundraiser) onOpenFundraiser(fid);
      else navigate(`/owner/fundraiser/${fid}`);
      return;
    }
    if (cta.tab && onNavigate) onNavigate(cta.tab);
  };

  const recordAction = async (insight, action) => {
    setPendingAction(insight.id);
    try {
      await apiClient.post("/intelligence/actions", {
        insightId: insight.id,
        action,
        category: insight.category,
        title: insight.title,
        estImpactGBP: insight.impactGBP || 0,
        firstSeenAt: firstSeenMap.current[insight.id] || null,
      });
      const msg = action === "done" ? "Marked done · value captured" : action === "snoozed" ? "Snoozed for 7 days" : "Ignored";
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to record action");
    } finally {
      setPendingAction(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-5 border-slate-200" data-testid="intel-loading">
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Scanning operational data…
        </div>
      </Card>
    );
  }
  if (!data || data.forbidden) return null;
  if (data.error) {
    return (
      <Card className="p-5 border-rose-200 bg-rose-50" data-testid="intel-error">
        <div className="text-sm text-rose-800">Intelligence unavailable: {data.error}</div>
      </Card>
    );
  }

  const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;

  return (
    <Card className="p-5 border-slate-200 bg-gradient-to-br from-slate-50 to-white" data-testid="taptrack-intelligence">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Brain className="h-5 w-5 text-indigo-600" />
          <h3 className="font-semibold text-slate-900">Engage Intelligence</h3>
          <span className="text-xs text-slate-500 ml-1">
            {data.count} action{data.count === 1 ? "" : "s"} · {fmtGBP(data.totalImpactGBP)} est. impact
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="intel-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Weekly summary strip (owner-only; silent 403 hides it) */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="intel-summary">
          <div className="rounded-md p-3 bg-emerald-50 border border-emerald-200" data-testid="intel-summary-completed">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-700">
              <CheckCircle2 className="h-3 w-3" /> Actions completed
            </div>
            <div className="text-lg font-semibold text-emerald-900 mt-0.5">{summary.actionsCompleted}</div>
            <div className="text-[11px] text-emerald-700">this week</div>
          </div>
          <div className="rounded-md p-3 bg-indigo-50 border border-indigo-200" data-testid="intel-summary-value">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-indigo-700">
              <TrendingUp className="h-3 w-3" /> Value captured
            </div>
            <div className="text-lg font-semibold text-indigo-900 mt-0.5">{fmtGBP(summary.valueCapturedGBP)}</div>
            <div className="text-[11px] text-indigo-700">est.</div>
          </div>
          <div className="rounded-md p-3 bg-amber-50 border border-amber-200" data-testid="intel-summary-open">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-700">
              <Sparkles className="h-3 w-3" /> Open opportunities
            </div>
            <div className="text-lg font-semibold text-amber-900 mt-0.5">{summary.openOpportunities}</div>
            <div className="text-[11px] text-amber-700">awaiting action</div>
          </div>
          <div className="rounded-md p-3 bg-slate-50 border border-slate-200" data-testid="intel-summary-response">
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-600">
              <Clock className="h-3 w-3" /> Avg response time
            </div>
            <div className="text-lg font-semibold text-slate-900 mt-0.5">{summary.avgResponseTimeHuman || "—"}</div>
            <div className="text-[11px] text-slate-600">{summary.actionsSnoozed} snoozed · {summary.actionsIgnored} ignored</div>
          </div>
        </div>
      )}

      {/* Manager leaderboard (owner-only; silent hide otherwise) */}
      {summary && summary.leaderboard && summary.leaderboard.length > 0 && (
        <div className="mb-4 p-3 rounded-md bg-white border border-slate-200" data-testid="intel-leaderboard">
          <div className="flex items-center gap-1.5 mb-2">
            <Trophy className="h-3.5 w-3.5 text-amber-600" />
            <div className="text-xs font-semibold text-slate-800">Manager Leaderboard · this week</div>
          </div>
          <div className="space-y-1">
            {summary.leaderboard.map((m, i) => (
              <div key={m.userId} className="flex items-center justify-between text-sm" data-testid={`intel-leaderboard-row-${i + 1}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] font-mono text-slate-400 w-4">#{i + 1}</span>
                  <Users className="h-3 w-3 text-slate-400 flex-shrink-0" />
                  <span className="truncate text-slate-800">{m.userName}</span>
                </div>
                <div className="flex items-center gap-3 text-xs flex-shrink-0">
                  <span className="font-semibold text-indigo-700">{fmtGBP(m.valueCaptured)}</span>
                  <span className="text-slate-500">{m.actionsCompleted} action{m.actionsCompleted === 1 ? "" : "s"}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.count === 0 ? (
        <div className="text-center py-8 text-slate-500 text-sm" data-testid="intel-empty">
          <Sparkles className="h-6 w-6 mx-auto text-slate-300 mb-2" />
          No priority actions detected. Your operation is running cleanly.
        </div>
      ) : (
        <div className="space-y-2" data-testid="intel-list">
          {(data.insights || []).map((ins, idx) => {
            const busy = pendingAction === ins.id;
            return (
              <div
                key={ins.id}
                className="group flex items-start gap-3 p-3 rounded-lg bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-sm transition-all"
                data-testid={`intel-item-${ins.id}`}
              >
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-50 text-indigo-700 flex items-center justify-center text-xs font-semibold">
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <CategoryPill category={ins.category} />
                    <ConfidenceBadge level={ins.confidence} />
                    <span className="text-[11px] font-mono text-slate-500">{fmtGBP(ins.impactGBP)}</span>
                  </div>
                  <div className="text-sm font-medium text-slate-900 leading-snug">{ins.title}</div>
                  {ins.summary && <div className="text-xs text-slate-500 mt-0.5">{ins.summary}</div>}
                  <div className="flex items-center gap-1 mt-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => recordAction(ins, "done")}
                      className="h-7 text-[11px] text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                      data-testid={`intel-action-done-${ins.id}`}
                    >
                      {busy ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                      Mark done
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => recordAction(ins, "snoozed")}
                      className="h-7 text-[11px] text-amber-700 border-amber-200 hover:bg-amber-50"
                      data-testid={`intel-action-snooze-${ins.id}`}
                    >
                      <Clock className="h-3 w-3 mr-1" /> Snooze 7d
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => recordAction(ins, "ignored")}
                      className="h-7 text-[11px] text-slate-500 hover:bg-slate-100"
                      data-testid={`intel-action-ignore-${ins.id}`}
                    >
                      <X className="h-3 w-3 mr-1" /> Ignore
                    </Button>
                  </div>
                </div>
                {ins.cta?.label && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCta(ins)}
                    className="flex-shrink-0 text-indigo-700 hover:bg-indigo-50 gap-1"
                    data-testid={`intel-cta-${ins.id}`}
                  >
                    {ins.cta.label}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
